import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Copy, Loader2, Users, Clock, Calendar, FileText, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useJobs } from '@/hooks/useJobs';
import { useVendorJobs } from '@/hooks/useVendorJobs';
import { useAuth } from '@/lib/auth';
import { useCopyJob } from '@/hooks/useCopyJob';
import { useToast } from '@/hooks/use-toast';
import { JobStatus, JobCategory, JOB_CATEGORIES, getStatusColor, formatCurrency, getAwardedSubStep, getSubStepConfig } from '@/lib/types/job';
import { JobsExportButton } from '@/components/jobs/JobsExportButton';

export default function JobsPage() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const { copyJob } = useCopyJob();
  
  const [search, setSearch] = useState('');
  const [copyingJobId, setCopyingJobId] = useState<string | null>(null);
  const ALL_STATUSES: JobStatus[] = ['draft','rfq_open','awarded','in_progress','completed_pending_review','accepted','rejected','disputed','closed'];
  const [selectedStatuses, setSelectedStatuses] = useState<JobStatus[]>(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const statusParam = sp.get('status');
      return statusParam && (ALL_STATUSES as string[]).includes(statusParam)
        ? [statusParam as JobStatus]
        : [];
    } catch {
      return [];
    }
  });
  const [selectedCategories, setSelectedCategories] = useState<JobCategory[]>([]);
  
  // Explicit role check
  const isVendor = user?.role === 'vendor';
  
  // Use different hooks based on user role
  const clientJobsData = useJobs({
    search: search || undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    category: selectedCategories.length > 0 ? selectedCategories : undefined,
    enabled: !isLoading && !isVendor,
  });

  const vendorJobsData = useVendorJobs({
    search: search || undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    enabled: !isLoading && isVendor,
  });

  // Helper function to calculate time remaining until RFQ closes
  const getTimeRemaining = (closesAt: string) => {
    const timeLeft = new Date(closesAt).getTime() - new Date().getTime();
    if (timeLeft <= 0) return 'Closed';
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const { jobs, loading, error, refetch } = isVendor ? vendorJobsData : clientJobsData;


  // Force refresh once auth is ready
  useEffect(() => {
    if (!isLoading) {
      refetch();
    }
  }, [isLoading]);

  // Different status options for vendor vs client
  const statuses: { value: JobStatus; label: string }[] = isVendor ? [
    { value: 'awarded', label: 'Awarded' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed_pending_review', label: 'Pending Review' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disputed', label: 'Disputed' },
    { value: 'closed', label: 'Closed' },
  ] : [
    { value: 'draft', label: 'Draft' },
    { value: 'rfq_open', label: 'RFQ Open' },
    { value: 'awarded', label: 'Awarded' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed_pending_review', label: 'Pending Review' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disputed', label: 'Disputed' },
    { value: 'closed', label: 'Closed' },
  ];

  const handleStatusChange = (status: JobStatus, checked: boolean) => {
    if (checked) {
      setSelectedStatuses([...selectedStatuses, status]);
    } else {
      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
    }
  };

  const handleCategoryChange = (category: JobCategory, checked: boolean) => {
    if (checked) {
      setSelectedCategories([...selectedCategories, category]);
    } else {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    }
  };

  const handleCopyJob = async (jobId: string, publicId: string) => {
    if (isVendor) return;
    
    setCopyingJobId(jobId);
    try {
      const newJobPublicId = await copyJob(jobId);
      toast({
        title: "Job Copied Successfully",
        description: `Created a copy of job ${publicId}`,
      });
      refetch();
    } catch (error) {
      toast({
        title: "Failed to Copy Job",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setCopyingJobId(null);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading jobs: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{isVendor ? 'Active Jobs' : 'Jobs'}</h1>
        <div className="flex items-center gap-2">
          <JobsExportButton isVendor={isVendor} />
          {!isVendor && (
            <Link to="/jobs/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Job
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Filters */}
        <aside className="w-full lg:w-64 space-y-6">
          <Card className="p-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search jobs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-3 block">Status</label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {statuses.map((status) => (
                    <div key={status.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={status.value}
                        checked={selectedStatuses.includes(status.value)}
                        onCheckedChange={(checked) => 
                          handleStatusChange(status.value, checked as boolean)
                        }
                      />
                      <label 
                        htmlFor={status.value}
                        className="text-sm cursor-pointer"
                      >
                        {status.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {!isVendor && (
                <div>
                  <label className="text-sm font-medium mb-3 block">Category</label>
                  <div className="space-y-2">
                    {JOB_CATEGORIES.map((category) => (
                      <div key={category.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={category.value}
                          checked={selectedCategories.includes(category.value)}
                          onCheckedChange={(checked) => 
                            handleCategoryChange(category.value, checked as boolean)
                          }
                        />
                        <label 
                          htmlFor={category.value}
                          className="text-sm cursor-pointer"
                        >
                          {category.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {loading ? (
            <div className="text-center py-8">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <Card className="p-8 text-center">
              <h3 className="text-lg font-medium mb-2">No {isVendor ? 'active jobs' : 'jobs'} found</h3>
              <p className="text-muted-foreground mb-4">
                {isVendor 
                  ? 'No jobs have been assigned to you yet. Jobs will appear here when you win RFQ bids.'
                  : 'Create your first job to get started.'
                }
              </p>
              {!isVendor && (
                <Link to="/jobs/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Job
                  </Button>
                </Link>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card key={job.id} className="p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Link 
                          to={`/jobs/${job.public_id}`}
                          className="text-xl font-semibold hover:text-primary transition-colors"
                        >
                          {job.title}
                        </Link>
                        {(() => {
                          // Only show "RFQ Closed" if job is still in RFQ phase
                          const isInRFQPhase = job.status === 'draft' || job.status === 'rfq_open';
                          const isRFQExpired = !!job.rfq && new Date(job.rfq.closes_at).getTime() <= new Date().getTime();
                          const isRFQClosed = !!job.rfq && (job.rfq.status === 'closed' || isRFQExpired);
                          
                          // Show actual job status unless it's in RFQ phase AND RFQ is closed
                          const displayStatus = (isInRFQPhase && isRFQClosed) ? 'rfq_closed' : job.status;
                          const statusLabel = (isInRFQPhase && isRFQClosed) ? 'RFQ Closed' : job.status.replace('_', ' ');
                          
                          return (
                            <Badge className={getStatusColor(displayStatus)}>
                              {statusLabel}
                            </Badge>
                          );
                        })()}
                        {/* Sub-step badge for awarded jobs */}
                        {job.status === 'awarded' && (() => {
                          const subStep = getAwardedSubStep(job);
                          if (!subStep) return null;
                          
                          const config = getSubStepConfig(subStep);
                          const IconMap = { Calendar, FileText, Send, Clock };
                          const Icon = IconMap[config.icon];
                          
                          return (
                            <Badge variant="outline" className={config.className}>
                              <Icon className="h-3 w-3 mr-1" />
                              {config.label}
                            </Badge>
                          );
                        })()}
                        {!isVendor && job.rfq && job.rfq.status === 'open' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                            <Users className="h-3 w-3 mr-1" />
                            {(job.rfq as any).bids?.length || 0} {(job.rfq as any).bids?.length === 1 ? 'bid' : 'bids'}
                          </Badge>
                        )}
                        {!isVendor && job.rfq && job.rfq.status === 'open' && job.rfq.closes_at && (
                          <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                            <Clock className="h-3 w-3" />
                            <span className="text-xs font-medium">
                              RFQ Closes: {getTimeRemaining(job.rfq.closes_at)}
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm mb-2">
                        ID: {job.public_id}
                      </p>
                      <p className="text-muted-foreground line-clamp-2">
                        {job.description}
                      </p>
                    </div>
                    <div className="text-right">
                      {/* Show value when a PO exists or job has been awarded */}
                      {(
                        job.purchase_order ||
                        (job.awards && job.awards.award_selections && job.awards.award_selections.length > 0)
                      ) && (
                        <div className="text-lg font-semibold">
                          {(() => {
                            // Defensive handling: purchase_order might be array or object (before UNIQUE constraint)
                            const po = Array.isArray(job.purchase_order)
                              ? job.purchase_order.sort((a, b) => 
                                  new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                                )[0]
                              : job.purchase_order;

                            // If PO exists, show PO total (living document with adjustments)
                            // Otherwise show award total (original contract value)
                            const amount = po?.po_amount_cents || 
                                          job.awards?.total_award_amount_cents ||
                                          job.awards?.award_selections?.reduce(
                                            (sum: number, sel: any) => sum + (sel.total_price_cents || 0), 
                                            0
                                          ) ||
                                          0;

                            return formatCurrency(amount);
                          })()}
                        </div>
                      )}
                      <div className="text-sm text-muted-foreground">
                        Due: {new Date(job.due_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                      <span>Category: {JOB_CATEGORIES.find(c => c.value === job.category)?.label}</span>
                      <span>Created: {new Date(job.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isVendor && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCopyJob(job.id, job.public_id);
                                }}
                                disabled={copyingJobId === job.id}
                              >
                                {copyingJobId === job.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Copy Job</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Link to={`/jobs/${job.public_id}`} className="flex-1 sm:flex-none">
                        <Button variant="outline" size="sm" className="w-full sm:w-auto">
                          View Details
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}